import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmt } from '../api';
import Chart from '../components/Chart';

const PLATFORM_COLORS: Record<string, string> = {
  meta: '#2f6af2',
  pinterest: '#d64862',
  reddit: '#ff6a25',
};

export default function Dashboard() {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.dashboard().then(setData).catch((e) => setError(e.message));
  }, []);

  if (error) return <div className="empty">Error: {error}</div>;
  if (!data) return <div className="empty">Loading…</div>;

  const { kpis, chartDaily, platformSplit, topCampaigns, topProducts, alerts } = data;

  // Pivot daily data to { day, meta: spend, pinterest: spend, reddit: spend }
  const dayMap = new Map<string, any>();
  for (const r of chartDaily) {
    if (!dayMap.has(r.day)) dayMap.set(r.day, { day: r.day, meta: 0, pinterest: 0, reddit: 0 });
    dayMap.get(r.day)[r.platform] = r.revenue_cents;
  }
  const chartData = [...dayMap.values()];

  return (
    <>
      <h2 className="page-title">Dashboard</h2>
      <p className="page-sub">Last 7 days — all platforms</p>

      <div className="kpi-row">
        <Kpi label="Spend (7d)" value={fmt.moneyShort(kpis.spend7_cents)} delta={kpis.deltas.spend} />
        <Kpi label="Revenue (7d)" value={fmt.moneyShort(kpis.revenue7_cents)} delta={kpis.deltas.revenue} />
        <Kpi
          label="ROAS (7d)"
          value={`${kpis.roas7.toFixed(2)}×`}
          delta={null}
          rightHint={kpis.roas7 >= 2 ? 'on target' : 'below target'}
        />
        <Kpi label="Orders (7d)" value={fmt.num(kpis.orders7)} delta={kpis.deltas.orders} />
        <Kpi label="CPA (7d)" value={kpis.cpa7_cents ? fmt.money(kpis.cpa7_cents) : '—'} delta={null} />
      </div>

      <div className="grid-2">
        <div className="card chart-wrap">
          <div className="card-title">
            Revenue by platform (14d)
            <span className="muted">stacked daily</span>
          </div>
          <Chart
            data={chartData}
            series={[
              { key: 'meta', color: PLATFORM_COLORS.meta, label: 'Meta' },
              { key: 'pinterest', color: PLATFORM_COLORS.pinterest, label: 'Pinterest' },
              { key: 'reddit', color: PLATFORM_COLORS.reddit, label: 'Reddit' },
            ]}
          />
        </div>
        <div className="card card-padded">
          <div className="card-title">Alerts & recommendations</div>
          {alerts.length === 0 ? (
            <div className="empty">All clear.</div>
          ) : (
            alerts.slice(0, 4).map((a: any) => (
              <div key={a.id} className={`rec-card ${a.kind}`}>
                <h4>{a.title}</h4>
                <p>{a.rationale}</p>
              </div>
            ))
          )}
          <Link to="/recommendations" className="btn btn-small" style={{ marginTop: 4 }}>
            View all recommendations →
          </Link>
        </div>
      </div>

      <div className="grid-2">
        <div className="card card-padded">
          <div className="card-title">
            Top campaigns (7d)
            <Link to="/campaigns" className="muted">view all</Link>
          </div>
          <table className="data">
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Status</th>
                <th className="num">Spend</th>
                <th className="num">Revenue</th>
                <th className="num">ROAS</th>
              </tr>
            </thead>
            <tbody>
              {topCampaigns.map((c: any) => (
                <tr key={c.id}>
                  <td>
                    <Link to={`/campaigns/${c.id}`}>{c.name}</Link>
                  </td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="num">{fmt.moneyShort(c.spend_cents ?? 0)}</td>
                  <td className="num">{fmt.moneyShort(c.revenue_cents ?? 0)}</td>
                  <td className="num">
                    {c.spend_cents > 0
                      ? `${(c.revenue_cents / c.spend_cents).toFixed(2)}×`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card card-padded">
          <div className="card-title">Top products (30d)</div>
          {topProducts.map((p: any) => (
            <div key={p.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
              {p.image_url && (
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 6,
                    background: `url(${p.image_url}) center/cover`,
                  }}
                />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-sub)' }}>
                  {fmt.num(p.units_sold_30d)} units · Ad-fit {p.ad_fit_score}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{fmt.moneyShort(p.revenue_30d_cents)}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="card card-padded" style={{ marginTop: 20 }}>
        <div className="card-title">Platform split (7d)</div>
        <PlatformSplit split={platformSplit} />
      </div>
    </>
  );
}

function Kpi({ label, value, delta, rightHint }: { label: string; value: string; delta: number | null; rightHint?: string }) {
  return (
    <div className="kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      <div className={`delta ${delta == null ? 'delta-flat' : delta >= 0 ? 'delta-up' : 'delta-down'}`}>
        {delta == null ? rightHint ?? '' : `${fmt.delta(delta)} vs prior 7d`}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`badge badge-${status}`}>{status}</span>;
}

function PlatformSplit({ split }: { split: any[] }) {
  const total = split.reduce((s, r) => s + (r.revenue_cents ?? 0), 0);
  if (!total) return <div className="empty">No spend yet</div>;
  return (
    <div>
      <div style={{ display: 'flex', height: 28, borderRadius: 8, overflow: 'hidden', background: 'var(--surface-2)' }}>
        {split.map((r) => {
          const pct = (r.revenue_cents ?? 0) / total;
          return (
            <div
              key={r.platform}
              style={{
                width: `${pct * 100}%`,
                background: PLATFORM_COLORS[r.platform] ?? '#999',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                fontSize: 11,
                fontWeight: 600,
              }}
            >
              {pct > 0.1 ? `${r.platform} ${fmt.pct(pct, 0)}` : ''}
            </div>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 20, marginTop: 12, fontSize: 12, color: 'var(--text-sub)' }}>
        {split.map((r) => (
          <div key={r.platform}>
            <span className={`badge badge-${r.platform}`}>{r.platform}</span>{' '}
            {fmt.moneyShort(r.revenue_cents)} rev ·{' '}
            {r.spend_cents > 0 ? (r.revenue_cents / r.spend_cents).toFixed(2) : '—'}× ROAS
          </div>
        ))}
      </div>
    </div>
  );
}
